import { useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import { StockCategoryFilter } from '../types/stockCategories';
import { useAuth } from './useAuth';

/**
 * Returns a Set of date strings (YYYY-MM-DD) for which the authenticated user
 * has already completed the game in the given category.
 * Anonymous users always get an empty set.
 */
export const useCompletedDays = (
  category: StockCategoryFilter,
  dates: string[]
): { completedDates: Set<string>; loading: boolean } => {
  const { user } = useAuth();
  const [completedDates, setCompletedDates] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.id || dates.length === 0) {
      setCompletedDates(new Set());
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const { data, error } = await supabase
          .from('game_plays')
          .select('game_date')
          .eq('user_id', user.id)
          .eq('category', category)
          .in('game_date', dates);

        if (!cancelled && !error && data) {
          setCompletedDates(new Set(data.map((r) => r.game_date)));
        }
      } catch {
        // silently ignore – no checkmarks is fine
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, category, dates]);

  return { completedDates, loading };
};
